import { Box, Tooltip, Typography } from "@mui/material";
import "../../styles/clientPage.css";
import { useEffect, useMemo, useState } from "react";
import ArrowUp from "../../assets/common/arrow-up-green.svg";
import ArrowDown from "../../assets/common/arrow-down-red.svg";
import { formatCurrency, normalizeToFullDay } from "../../utils/utils";
import { addDays, format } from "date-fns";
import SlideOver from "../Common/SlideOver";
import ClientStatsBusinessOverview from "../ClientOverview/ClientBusinessStats/ClientStatsBusinessOverview";
import useGlobalContext from "../../context/useGlobalContext";
import { useLocation, useNavigate } from "react-router-dom";
import { StatConfig } from "../../utils/types";

interface ClientStatProps {
  clientStatsData: any;
  statConfigs: StatConfig[];
  showSpinner?: boolean;
  parentPageIdentifier?: string;
  headerTitle?: string;
  timeRangeLabel?: string;
  slideOverParent?: string; // allow parent to override
  userEmail?: string;
}

const ClientStats = ({
  clientStatsData,
  statConfigs,
  showSpinner,
  parentPageIdentifier,
  headerTitle = "Business Stats",
  timeRangeLabel = "Trailing 4-weeks",
  slideOverParent = "client-stats",
  userEmail,
}: ClientStatProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSlideOverOpen, setIsSlideOverOpen] = useState( false);
  const [activeStat, setActiveStat] = useState<string | null>(null);
  const {setDateRange} = useGlobalContext();
  const incomingSlideOverParent = location.state?.slideOverParent;
  const [hoveredWeekData, setHoveredWeekData] = useState<
  Record<string, { weekStart: string; value: number | string; change: number; valueRaw?: number }>
>({});
  const incomingActiveStat = location.state?.activeStat;
  const incomingCustomDateRange = location.state?.customDateRange;
  const savedFilters = localStorage.getItem("businessOverviewFilters");
  const initialDateRange = savedFilters
    ? JSON.parse(savedFilters)
    : incomingCustomDateRange;

  const [customDateRange, setCustomDateRange] = useState<{ startDate: string; endDate: string } | undefined>(initialDateRange);
  useEffect(() => {
    if (customDateRange) {
      localStorage.setItem("businessOverviewFilters", JSON.stringify(customDateRange));
    }
  }, [customDateRange]);
  useEffect(() => {
    if (incomingSlideOverParent === slideOverParent) {
      // Decide which stat to open by default, e.g., "clientsMet"
      setActiveStat(incomingActiveStat);
      setIsSlideOverOpen(true);
      if (incomingCustomDateRange) {
          setCustomDateRange(incomingCustomDateRange);
        }   
    }
  }, [incomingSlideOverParent]);
  const computeChange = (current: number, prev: number): number => {
    if (prev === 0) return current === 0 ? 0 : 100;
    return ((current - prev) / prev) * 100;
  };

  const formatTimeFromSeconds = (seconds: number): string => {
    if (seconds <= 0) return "0m";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      if (minutes === 0) return `${hours}h`;
      return `${hours}h ${minutes}m`;
    }

    if (minutes > 0) {
      if (secs === 0) return `${minutes}m`;
      return `${minutes}m ${secs}s`;
    }

    if (secs > 0) return `${secs}s`;
    return "0m";
  };

  const formatStatValue = (type: "count" | "currency" | "time", value: number) => {
    if (type === "currency") return formatCurrency(value);
    if (type === "time") return formatTimeFromSeconds(value);
    return value; // clientsMet or other counts
  };

  const statsData = useMemo(() => {
    const data = clientStatsData?.data || [];
    if (data.length < 2) return [];
    const weeks = [...data]
      .filter((w) => w?.weekStart) // ✅ filter out bad entries
      .sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime())
      .slice(0, 5)
      .reverse();     // oldest → latest

    const current = weeks[weeks.length - 1] ?? null;               // latest
    const previous = weeks.length > 1 ? weeks[weeks.length - 2] ?? null : null; // previous week    


    const generateBarData = (key: string) => {
      if (!weeks.length) return []; // ✅ guard
      const visibleWeeks = weeks.slice(1);
      const values = visibleWeeks.map((week: any) => {
        const raw = week[key] || 0;
        return raw;
      });
      const max = Math.max(...values);
      const safeMax = max === 0 ? 1 : max;


      return weeks.map((week: any, i: number) => {
        const raw = week[key] || 0;
        const val = raw;
        const height = i === 0 ? 5 : (val / safeMax) * 100;
        return { height, value: val, weekStart: week.weekStart };
      });
    };

    return statConfigs.map((cfg) => {
      const currVal = current?.[cfg.valueKey] ?? 0;
      const prevVal = previous?.[cfg.valueKey] ?? 0;

      return {
        key: cfg.key,
        title: cfg.title,
        value: cfg.type === "time" ? formatTimeFromSeconds(currVal) : formatStatValue(cfg.type, currVal),
        change: computeChange(currVal, prevVal).toFixed(2),
        bars: generateBarData(cfg.valueKey),
        type: cfg.type,
      };
    });
  }, [clientStatsData, statConfigs]);


  
  useEffect(() => {
    if (statsData?.length > 0) {
      const initialData: Record<string, any> = {};
      statsData?.forEach((stat) => {
        const lastIndex = stat.bars.length - 1;
        const lastBar = stat.bars[lastIndex];
        const prevBar = lastIndex > 0 ? stat.bars[lastIndex - 1] : null;
  
        const change = prevBar
          ? Math.round(computeChange(lastBar.value, prevBar.value))
          : 0;
  
        initialData[stat.key] = {
          weekStart: lastBar.weekStart,
          value: formatStatValue(stat.type, lastBar.value),
          valueRaw: lastBar.value,
          change, // ✅ now stores latest week’s actual % change
        };
      });
      setHoveredWeekData(initialData);
    }
  }, [statsData]);  

  const handleBarHover = (stat: { key: string; type: "count" | "currency" | "time" }, bars: any[], index: number) => {
    if (!bars || !bars[index]) return;
    const current = bars[index];
    const prev = index > 0 ? bars[index - 1] : null;
    const change = prev ? Math.round(computeChange(current.value, prev.value)) : 0;
  
    setHoveredWeekData((prevState) => ({
      ...prevState,
      [stat.key]: {
        weekStart: current.weekStart,
        value: formatStatValue(stat.type, current.value),
        valueRaw: current.value,
        change,
      },
    }));
  };
  
  const handleBarLeave = (stat: { key: string; type: "count" | "currency" | "time" }, bars: any[]) => {
    if (!bars || bars.length === 0) return;
    const lastIndex = bars.length - 1;
    const lastBar = bars[lastIndex];
    const prevBar = lastIndex > 0 ? bars[lastIndex - 1] : null;
  
    const latestChange = prevBar ? Math.round(computeChange(lastBar.value, prevBar.value)) : 0;
  
    setHoveredWeekData((prevState) => ({
      ...prevState,
      [stat.key]: {
        weekStart: lastBar.weekStart,
        value: formatStatValue(stat.type, lastBar.value),
        change: latestChange,
      },
    }));
  };  
  const handleCardClick = (
    statKey: string,
    weekStart: string,
    type?: "count" | "currency" | "time"
  ) => {
    if (statKey === "actualDuration" || statKey === "userTalkTime") {
      return;
    }

    const start = new Date(weekStart);
    const { startDate, endDate } = normalizeToFullDay(start, addDays(start, 6));

    const formattedStart = startDate.toISOString();
    const formattedEnd = endDate.toISOString();

    // NAVIGATION (count + currency)
    if (type === "count" || type === "currency") {
      navigate("/accounts/customer", {
        state: {
          customDateRange: {
            startDate: formattedStart,
            endDate: formattedEnd,
          },
          status: ["active"],
          isInteractionPresent: true,
        },
      });
      return;
    }

    setActiveStat(statKey);
    setIsSlideOverOpen(true);

    setDateRange({
      startDate: new Date(formattedStart),
      endDate: new Date(formattedEnd),
    });

    setCustomDateRange({
      startDate: formattedStart,
      endDate: formattedEnd,
    });
  };

  const handleStatsSlideOverClose = () => {
    setIsSlideOverOpen(false);
    setActiveStat(null);
  };
  return (
    <div className="client-stats-block__section">
      <div className="client-stats-block__header">
        <h1 className="client-stats-block__title">{headerTitle}</h1>
        <h1 className="client-stats-block__dropdown">{timeRangeLabel}</h1>
      </div>

      <div className="client-stats-block__grid">
      {showSpinner
    ? Array.from({ length: statConfigs.length || 4 }).map((_, index) => (
        <div className="client-stats-block__card" key={`shimmer-${index}`}>
          <div className="client-stats-block__card-content">

            <div className="client-stats-block__card-left">
              <div className="client-stats-block__card-header">
                <div className="shimmer-label" />
                <div className="shimmer-value" />
              </div>
              <div className="shimmer-change" />
            </div>

            <Box className="client-stats-block__card-right">
              <Box className="client-stats-block__bars-container">
                {Array.from({ length: 4 }).map((_, barIdx) => (
                  <Box
                    key={barIdx}
                    sx={{ height: "80px", width: "21px" }}
                    className="client__stats__bar shimmer-vertical-bar"
                  />
                ))}
              </Box>
            </Box>

          </div>
        </div>
      )) :
        statsData?.map((item, index) => {

          return (
            <div className="client-stats-block__card" key={index}>
              <div className="client-stats-block__card-content">
                <div className="client-stats-block__card-left">
                  <div className="client-stats-block__card-header">
                    <span className="client-stats-block__card-title">{item.title}</span>
                    <span className="client-stats-block__card-value" 
                    onClick={() =>
                      handleCardClick(item.key, item.bars[item.bars.length - 1].weekStart, item.type)
                    }>{hoveredWeekData[item.key]?.value ?? item.value}</span>
                  </div>

                  <div className="client-stats-block__card-footer">
                {(() => {
                  const currentChange: number =
                  hoveredWeekData[item.key]?.change ?? item.change;
                const isPositive = currentChange >= 0;
                const hoveredWeekStart = hoveredWeekData[item.key]?.weekStart;
                const hoveredIndex = item.bars.findIndex((bar: any) => bar.weekStart === hoveredWeekStart);
                const currentIndex = hoveredIndex !== -1 ? hoveredIndex : item.bars.length - 1;
                const prevVal = item.bars[currentIndex - 1]?.value ?? 0;
                const currentVal = item.bars[currentIndex]?.value ?? 0;
                const isNewIncrease = prevVal === 0 && currentVal > 0;
                return (
                  <>
                    <span
                      className="client-stats-block__card-change"
                      style={{ color: isPositive ? "#1BB761" : "#FF4D4D" }}
                    >
                      {currentChange}%
                      {isNewIncrease && <span style={{ marginLeft: "2px" }}>+</span>}
                    </span>
                    <img
                      src={isPositive ? ArrowUp : ArrowDown}
                      alt={isPositive ? "Up" : "Down"}
                      style={{
                        width: 13,
                        height: 10,
                        marginLeft: "4px",
                        objectFit: "contain",
                      }}
                    />
                  </>
                );
              })()}
            </div>
                </div>

                <Box className="client-stats-block__card-right">
                <Box className="client-stats-block__bars-container"
                 onMouseLeave={() => handleBarLeave(item, item.bars)}
                >
                {item.bars.map((bar: any, i: number) => {
              if (i === 0) return null;
              const isLastWeek = i === item.bars.length - 1 && bar.value > 0;
              const displayTime = formatTimeFromSeconds(bar.value);

              return (
                <Tooltip
                  key={i}
                  title={
                    !showSpinner && (
                      <>
                        <div>
                          Week Ending:{" "}
                          {format(
                            addDays(new Date(bar.weekStart), 6),
                            "MMM d, yyyy"
                          )}
                        </div>
                
                        <div>
                          {item.title}:{" "}
                          {item.title.includes("MRR")
                            ? `${formatCurrency(bar.value)}`
                            : item.type === "time"
                            ? `${displayTime}`
                            : bar.value}
                        </div>
                      </>
                    )
                  }
                  arrow
                >
                   <Box className="client__stats__bar-wrapper">

                    <Box className="client__stats__bar-container">
                      <Box
                        onClick={() => handleCardClick(item.key, bar.weekStart, item.type)}
                        onMouseEnter={() => handleBarHover(item, item.bars, i)}
                        sx={{
                          height: bar.value === 0 ? "5px" : `${bar.height}%`,
                          width: "21px",
                          borderRadius: "4px",
                          background:
                            hoveredWeekData[item.key]?.weekStart === bar.weekStart && isLastWeek
                              ? "linear-gradient(180deg, #805EDE 0%, #453378 100%)"
                              : "#EFEBFB",
                          transition: "0.3s",
                          cursor: "pointer",
                        }}
                        className="client__stats__bar"
                      />
                    </Box>

                    <Typography className="client__stats__bar-label">
                      <span className="client__stats__bar-label-w">W</span>
                      <span className="client__stats__bar-label-num">-{item.bars.length - i}</span>
                    </Typography>

                    </Box>

                </Tooltip>
              );
            })}
              </Box>
              </Box>
              </div>
            </div>
          );
        })}
      </div>
      <SlideOver isOpen={isSlideOverOpen}
       onClose={handleStatsSlideOverClose}>
      <ClientStatsBusinessOverview 
      isSlideOverOpen={isSlideOverOpen} 
      slideOverParent={slideOverParent} 
      activeStat ={activeStat}
      parentPageIdentifier={parentPageIdentifier}
      customDateRange={customDateRange}
      userEmail={userEmail}
      />
      </SlideOver>
    </div>
  );
};

export default ClientStats;
